// Verification utility helper functions extracted from Verification.tsx
// Phase 1 extraction: small, mostly pure helpers

// NOTE: Import type & enum dependencies. Adjust paths if these live elsewhere.
// Using type-only imports where appropriate to avoid runtime impact.
import { SelectOption } from '../../types/ViewModel/SelectOption';
import type { QueryParamValueProps } from 'odfjell-query-manager';
import type { Vessel } from '../../types/ViewModel/Vessel/Vessel';
import type { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import type { DnvDataItem } from '../../types/ViewModel/Verification/DnvDataItem';
import type { VerificationFilters } from '../../types/ViewModel/Verification/VerificationFilters';

// External helpers (reuse existing verification utils for status/type strings & date helper)
import { getProcessStatusString, getReportTypeString, findMaxdate } from '../../utils/verification/verificationUtils';

// Constants
export const VERIFICATION_START_YEAR = 2023;

/**
 * Get the default year options for verification filters.
 * Before April: includes previous year and current year.
 * April onwards: includes only current year.
 * @returns Object with yearsAll (all available years) and yearsDefault (default selection)
 */
export const getDefaultYearOptions = (): { yearsAll: SelectOption[]; yearsDefault: SelectOption[] } => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const defaultStartYear = currentMonth < 4 ? currentYear - 1 : currentYear;

    const yearsAll: SelectOption[] = [];
    for (let year = currentYear; year >= VERIFICATION_START_YEAR; year--) {
        yearsAll.push(new SelectOption(year.toString(), year.toString()));
    }

    const yearsDefault: SelectOption[] = [];
    for (let year = defaultStartYear; year <= currentYear; year++) {
        yearsDefault.push(new SelectOption(year.toString(), year.toString()));
    }

    return { yearsAll, yearsDefault };
};

/**
 * Get the default start year for verification data loading.
 * Before April 1st: previous year
 * April 1st onwards: current year
 */
export const getDefaultStartYear = (): number => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    return currentMonth < 4 ? now.getFullYear() - 1 : now.getFullYear();
};

// 1. Determine if any query filter params are present
export const hasQueryFilterParams = (params: QueryParamValueProps[]): boolean => {
    for (const param of params) {
        if (
            (param.name === 'managers' && param.newValue) ||
            (param.name === 'users' && param.newValue) ||
            (param.name === 'vessels' && param.newValue) ||
            (param.name === 'years' && param.newValue) ||
            (param.name === 'reportTypes' && param.newValue) ||
            (param.name === 'reportStatuses' && param.newValue) ||
            (param.name === 'checks' && param.newValue)
        ) {
            return true;
        }
    }
    return false;
};

// 2. Generic conversion of CSV to SelectOption array (label=value)
export const makeOptionsArray = (selectOptionsStr: string): SelectOption[] => {
    if (!selectOptionsStr) return [];
    return selectOptionsStr
        .split(',')
        .filter(Boolean)
        .map((o) => new SelectOption(o, o));
};

// 3. CSV to status options
export const makeOptionsStatusesArray = (selectOptionsStr: string): SelectOption[] => {
    if (!selectOptionsStr) return [];
    return selectOptionsStr
        .split(',')
        .filter(Boolean)
        .map((o) => new SelectOption(o, getProcessStatusString(Number(o))));
};

// 4. CSV to report type options
export const makeOptionsTypesArray = (selectOptionsStr: string): SelectOption[] => {
    if (!selectOptionsStr) return [];
    return selectOptionsStr
        .split(',')
        .filter(Boolean)
        .map((o) => new SelectOption(o, getReportTypeString(Number(o))));
};

// 5. CSV to vessel options (requires vessel list for name lookup)
export const makeOptionsVesselsArray = (selectOptionsStr: string, vesselList: Vessel[]): SelectOption[] => {
    if (!selectOptionsStr) return [];
    const mapByImo = new Map(vesselList.map((v) => [v.imoNumber.toString(), v.vesselName] as const));
    return selectOptionsStr
        .split(',')
        .filter(Boolean)
        .map((o) => new SelectOption(o, mapByImo.get(o)));
};

// 6. Collect unique users assigned to vessels' reports
export const getUsersAssignedReports = (vesselList: Vessel[]): string[] => {
    const users: string[] = [];
    for (const v of vesselList) {
        const u = (v as any).fleetPerformanceAlertResponsible; // adjust property if typed differently
        if (u && !users.includes(u)) users.push(u);
    }
    return users;
};

// 7. Process verification data for a report (mutating original for now to preserve behavior)
export const processVerificationsForReport = (reportMetadata: ReportMetadata, dnvIssuesList?: DnvDataItem[] | null) => {
    const list: DnvDataItem[] = Array.isArray(dnvIssuesList) ? dnvIssuesList : [];
    if (list.length > 0) {
        const maxDateStr = findMaxdate(list);
        if (maxDateStr) {
            (reportMetadata as any).maxVerificationDate = new Date(maxDateStr);
        }
    }
    const issuesList = list.filter((v) => v.severity === 'Issue');
    const warningsList = list.filter((v) => v.severity === 'Warning');

    (reportMetadata as any).numIssues = issuesList.length;
    (reportMetadata as any).numWarnings = warningsList.length;
    (reportMetadata as any).verificationIssuesList = issuesList;
    (reportMetadata as any).verificationWarningsList = warningsList;
    return reportMetadata;
};

// Phase 2: Pure builder for default verification filters (no store side-effects)
export interface BuildDefaultFiltersInput {
    filters: VerificationFilters; // existing (possibly partially null) filters
    vesselList: Vessel[];
    populatedReportMetadataList: ReportMetadata[];
    getProcessStatusStringFn?: (s: number) => string; // allow injection for test
    getReportTypeStringFn?: (t: number) => string; // allow injection for test
}

export interface BuildDefaultFiltersResult {
    updatedFilters: VerificationFilters;
    derived: {
        statusesAll: SelectOption[];
        statusesDefault: SelectOption[];
        yearsAll: SelectOption[];
        yearsDefault: SelectOption[];
        typesAll: SelectOption[];
        vesselsAll: SelectOption[];
        managersAll: SelectOption[];
        usersAll: SelectOption[];
        checksAll: SelectOption[];
    };
}

// Minimal shape for VerificationFilters (local extraction to avoid circular import). Extend if needed.
// If a global type exists, replace this with an import.
// Using project-wide VerificationFilters type (imported). Ensure optional availableChecks normalized below.

export const buildDefaultFilters = (input: BuildDefaultFiltersInput): BuildDefaultFiltersResult => {
    const {
        filters,
        vesselList,
        populatedReportMetadataList,
        getProcessStatusStringFn = getProcessStatusString,
        getReportTypeStringFn = getReportTypeString,
    } = input;
    const f: VerificationFilters = { ...filters };
    if (typeof f.availableChecks === 'undefined') {
        f.availableChecks = null;
    }

    // Statuses
    const statusesAll: SelectOption[] = [
        new SelectOption('0', getProcessStatusStringFn(0)),
        new SelectOption('1', getProcessStatusStringFn(1)),
        new SelectOption('2', getProcessStatusStringFn(2)),
        new SelectOption('3', getProcessStatusStringFn(3)),
        new SelectOption('4', getProcessStatusStringFn(4)),
        new SelectOption('5', getProcessStatusStringFn(5)),
        new SelectOption('6', getProcessStatusStringFn(6)),
    ];
    const statusesDefault = statusesAll.filter((o) => ['1', '2', '4', '6'].includes(o.value));
    if (f.selectedStatuses === null) {
        f.selectedStatuses = statusesDefault;
        f.initialSelectedStatuses = statusesAll;
    }

    // Years
    const currentYear = new Date().getFullYear();
    const yearsAll: SelectOption[] = [];
    for (let year = currentYear; year >= 2023; year--) yearsAll.push(new SelectOption(year.toString(), year.toString()));
    const currentMonth = new Date().getMonth() + 1;
    const defaultStartYear = currentMonth < 4 ? currentYear - 1 : currentYear;
    const yearsDefault: SelectOption[] = [];
    for (let year = defaultStartYear; year <= currentYear; year++) yearsDefault.push(new SelectOption(year.toString(), year.toString()));
    if (f.selectedYears === null) {
        f.selectedYears = yearsDefault;
        f.initialSelectedYears = yearsAll;
    }

    // Report types
    const typesAll: SelectOption[] = [
        new SelectOption('0', getReportTypeStringFn(0)),
        new SelectOption('1', getReportTypeStringFn(1)),
        new SelectOption('2', getReportTypeStringFn(2)),
        new SelectOption('3', getReportTypeStringFn(3)),
    ];
    if (f.selectedTypes === null) {
        f.selectedTypes = typesAll;
        f.initialSelectedTypes = typesAll;
    }

    // Vessels participating in reports
    const vesselsInReports = vesselList
        .slice()
        .sort((a, b) => (a.vesselName > b.vesselName ? 1 : -1))
        .filter((v) => populatedReportMetadataList.find((r) => r.imoNumber === v.imoNumber));
    const vesselsAll: SelectOption[] = vesselsInReports.map((v) => new SelectOption(v.imoNumber.toString(), v.vesselName));
    if (f.selectedVessels === null) {
        f.selectedVessels = vesselsAll;
        f.initialSelectedVessels = vesselsAll;
    }

    // Managers
    const managersSet = new Set<string>();
    vesselsInReports.forEach((v) => v.technicalOperator && managersSet.add(v.technicalOperator));
    const managersAll: SelectOption[] = Array.from(managersSet).map((m) => new SelectOption(m, m));
    if (f.selectedManagers === null) {
        f.selectedManagers = managersAll;
        f.initialSelectedManagers = managersAll;
    }

    // Users
    const usersSet = new Set<string>();
    vesselsInReports.forEach((v) => (v as any).fleetPerformanceAlertResponsible && usersSet.add((v as any).fleetPerformanceAlertResponsible));
    const usersAll: SelectOption[] = Array.from(usersSet).map((u) => new SelectOption(u, u));
    if (f.selectedUsers === null) {
        f.selectedUsers = usersAll;
        f.initialSelectedUsers = usersAll;
    }

    // Checks
    const allChecksMap: Map<string, string> = new Map();
    populatedReportMetadataList?.forEach((r) => {
        r.dnvIssues?.forEach((i) => {
            if (i.checkNumber != null) {
                const key = i.checkNumber.toString();
                if (!allChecksMap.has(key)) {
                    allChecksMap.set(key, i.checkName || key);
                }
            }
        });
    });
    const checksAll: SelectOption[] = Array.from(allChecksMap.entries())
        .sort((a, b) => (Number(a[0]) > Number(b[0]) ? 1 : -1))
        .map(([num, name]) => new SelectOption(num, name));
    if (f.selectedChecks === null) {
        f.availableChecks = checksAll;
    }

    return {
        updatedFilters: f,
        derived: {
            statusesAll,
            statusesDefault,
            yearsAll,
            yearsDefault,
            typesAll,
            vesselsAll,
            managersAll,
            usersAll,
            checksAll,
        },
    };
};

export default {
    hasQueryFilterParams,
    makeOptionsArray,
    makeOptionsStatusesArray,
    makeOptionsTypesArray,
    makeOptionsVesselsArray,
    getUsersAssignedReports,
    processVerificationsForReport,
    buildDefaultFilters,
};
